import { dialogBackground } from '@components/calendar/dialogs/planDialogSurface';
import styled, { css, keyframes } from 'styled-components';

import { Column, Row } from '@components/common/layoutUtils';
import colors from '@utils/colors';
import { LargeText, RegularText, SmallText } from '@utils/typography';

import { ChipTone } from './types';

// Dark text on the light tint, light text on the dark one.
const pillText = (props: any) => (props.theme.name === 'light' ? props.theme.primaryDark : props.theme.primaryLight);

const flash = keyframes`
  0% { background-color: transparent; }
  30% { background-color: var(--flash-color, ${colors.lightPositive}); }
  100% { background-color: transparent; }
`;

export const EstimatorRoot = styled(Column)`
  width: 100%;
  min-height: 0;
  gap: 0;
`;

export const TitleBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 24px 28px 12px 0;
  box-sizing: border-box;
`;

export const TitleBarSide = styled(Row)`
  align-items: center;
  gap: 8px;
  min-width: 0;

  &:last-child {
    justify-content: flex-end;
  }
`;

export const CourseColor = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${props => props.$color};
`;

export const HeadTitle = styled(LargeText)`
  display: block;
  text-align: center;
  margin: 0;
`;

export const HeadSub = styled(RegularText)`
  color: ${props => props.theme.disabledTextColor};
`;

export const HeadCount = styled(RegularText)`
  color: ${props => props.theme.disabledTextColor};
  white-space: nowrap;
  text-align: right;

  b {
    color: ${props => props.theme.textStrongColor};
    font-weight: 700;
  }
`;

export const ProgressSticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  background: ${props => dialogBackground(props.theme)};
  padding: 12px 0 8px;
`;

export const ProgressTrack = styled.div`
  height: 5px;
  background: ${props => props.theme.borderColor};
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${props => props.$pct}%;
  background: ${colors.positive};
  border-radius: 3px;
  transition: width 0.35s ease;
`;

export const BulkBar = styled.div<{ $open: boolean }>`
  position: sticky;
  top: 25px;
  z-index: 5;
  display: grid;
  grid-template-rows: ${props => (props.$open ? '1fr' : '0fr')};
  margin-bottom: ${props => (props.$open ? '16px' : '0')};
  transition: grid-template-rows 0.3s ease, margin-bottom 0.3s ease;
`;

export const BulkBarClip = styled.div`
  overflow: hidden;
  min-height: 0;
`;

export const BulkBarInner = styled(Row)<{ $open: boolean }>`
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  background: ${props => (props.theme.name === 'light' ? `${props.theme.primaryLight}F5` : props.theme.backgroundDark)};
  border: 1px solid ${props => props.theme.primary};
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 0 0 8px ${props => props.theme.background};
  opacity: ${props => (props.$open ? 1 : 0)};
  transition: opacity 0.2s ease;
  transition-delay: ${props => (props.$open ? '0.06s' : '0s')};
`;

export const BulkError = styled.div`
  width: 100%;
  font-size: 12px;
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
  color: ${colors.warning};
  background: ${colors.lightWarning};
  border: 1px solid ${colors.warning};
  border-radius: 6px;
  padding: 6px 10px;
`;

// A heading over the book cards rather than a frame round them: purple marks the card to answer next.
export const ReadingsGroup = styled.div`
  margin: 22px 0 0;
`;

export const ReadingsGroupLabel = styled(Row)`
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
  padding: 0 2px;
`;

export const SectionCard = styled.div`
  margin-bottom: 22px;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 10px;
  overflow: hidden;
`;

export const CatHeader = styled(Row)`
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${props => props.theme.backgroundDark};
  border-bottom: 1px solid ${props => props.theme.borderColor};
`;

export const CatTitleWrap = styled(Row)`
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const CatMeta = styled(SmallText)`
  color: ${props => props.theme.disabledTextColor};
  white-space: nowrap;
`;

export const HeaderActions = styled(Row)`
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const TextBtn = styled.button<{ $tone?: 'override' }>`
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Quicksand', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 6px;
  color: ${props => (props.$tone === 'override' ? props.theme.primary : props.theme.disabledTextColor)};

  &:hover {
    color: ${props => props.theme.textStrongColor};
  }
`;

export const DefaultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: ${props => props.theme.background};
  border-bottom: 1px solid ${props => props.theme.borderColor};
`;

export const DefaultLabel = styled(SmallText)`
  width: 100%;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${props => props.theme.disabledTextColor};
`;

// The open list's first row: select all on the left, the card's reset on the right, so neither sits in the card
// header and moves the title between the folded and open views.
export const ListToolbarRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  padding: 8px 18px;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  background: ${props => props.theme.backgroundLight};
`;

export const ListToolbarSelect = styled(Row)`
  align-items: center;
  gap: 10px;
`;

export const ListToolbarLabel = styled(SmallText)`
  color: ${props => props.theme.disabledTextColor};
  cursor: pointer;
`;

export const Toast = styled.div<{ $warn?: boolean }>`
  font-size: 11.5px;
  font-family: 'Quicksand', sans-serif;
  color: ${props => (props.$warn ? colors.negative : colors.positive)};
  font-style: italic;
  width: 100%;
  min-height: 15px;
`;

export const TaskList = styled.div`
  padding: 2px 0;
`;

export const TaskRow = styled(Row)<{ $flash?: boolean; $open?: boolean }>`
  position: relative;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 18px;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  // Tinted while its estimate popup is open, so the popup plainly belongs to it.
  background: ${props => (props.$open ? `${props.theme.primary}12` : 'transparent')};
  --flash-color: ${props => (props.theme.name === 'light' ? colors.lightPositive : `${colors.positive}40`)};
  animation: ${props =>
    props.$flash
      ? css`
          ${flash} 0.65s ease
        `
      : 'none'};

  &:last-child {
    border-bottom: none;
  }
`;

export const TaskLeft = styled(Row)`
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

export const TaskRightGroup = styled(Row)`
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const EditableName = styled.span<{ $trashed?: boolean }>`
  font-family: 'Quicksand', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: ${props => props.theme.textStrongColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 1px dotted transparent;
  ${props =>
    props.$trashed &&
    css`
      opacity: 0.55;
      text-decoration: line-through;
    `}

  &:hover {
    border-bottom-color: ${props => props.theme.disabledTextColor};
  }
`;

export const TaskName = styled.span<{ $trashed?: boolean }>`
  font-family: 'Quicksand', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: ${props => props.theme.textStrongColor};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.12s ease;
  ${props =>
    props.$trashed &&
    css`
      opacity: 0.55;
      text-decoration: line-through;
    `}
`;

// A task's name and what's beside it open the task, as an Auto-plan row does; the name turns the theme colour on hover.
export const TaskOpen = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  align-self: stretch;
  cursor: pointer;

  &:hover ${TaskName} {
    color: ${props => props.theme.primary};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const CatTitleName = styled(EditableName)`
  font-size: 16px;
  font-weight: 700;
`;

export const NameInput = styled.input`
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
  font-size: 13.5px;
  color: ${props => props.theme.textStrongColor};
  border: 1px solid ${props => props.theme.disabledTextColor};
  border-radius: 4px;
  padding: 1px 5px;
  background: ${props => props.theme.background};
  outline: none;
  min-width: 8ch;
  max-width: 40ch;
`;

export const CatTitleInput = styled(NameInput)`
  font-size: 16px;
  font-weight: 700;
`;

export const EmojiBtn = styled.div`
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    transform: scale(1.1);
  }
`;

const chipTone = (tone: ChipTone) => {
  switch (tone) {
    // "Estimate" and "Set pages" on a task that needs its own answer: the pills' purple action, not a warning.
    case 'unset':
      return css`
        color: ${pillText};
        border-color: ${props => `${props.theme.primary}59`};
        background: ${props => `${props.theme.primary}1F`};
        border-style: solid;

        &:hover {
          color: ${colors.white};
          border-color: ${props => props.theme.primary};
          background: ${props => props.theme.primary};
        }
      `;
    // A task in a category still without a time: a grey dashed button, plainly clickable but quieter than the
    // category's own question above it.
    case 'quiet':
      return css`
        color: ${props => props.theme.textColor};
        border-color: ${props => props.theme.disabledTextColor};
        border-style: dashed;
        background: none;

        &:hover {
          color: ${props => props.theme.primary};
          border-color: ${props => props.theme.primary};
        }
      `;
    case 'inherited':
    case 'explicit':
      return css`
        color: ${colors.positive};
        border-color: ${colors.positive};
        background: ${props => (props.theme.name === 'light' ? colors.lightPositive : `${colors.positive}26`)};
        border-style: solid;
      `;
    case 'excluded':
      return css`
        color: ${props => props.theme.disabledTextColor};
        border-color: ${props => props.theme.borderColor};
        background: ${props => props.theme.backgroundDark};
        border-style: dashed;
        text-decoration: line-through;
      `;
    default:
      return '';
  }
};

export const Chip = styled.button<{ $tone: ChipTone }>`
  font-family: 'Quicksand', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 7px;
  cursor: pointer;
  border: 1px dashed ${props => props.theme.borderColor};
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
  ${props => chipTone(props.$tone)}
`;

export const ChipDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
`;

export const ChipX = styled.span`
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  margin-left: 1px;
  opacity: 0.65;

  &:hover {
    opacity: 1;
  }
`;

export const PillGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

export const Pill = styled.button<{ $selected?: boolean; $positive?: boolean }>`
  font-family: 'Quicksand', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.borderColor};
  background: ${props => props.theme.background};
  color: ${props => props.theme.textStrongColor};
  cursor: pointer;
  line-height: normal;

  &:hover {
    border-color: ${props => props.theme.disabledTextColor};
  }

  ${props =>
    props.$selected &&
    (props.$positive
      ? css`
          background: ${colors.positive};
          border-color: ${colors.positive};
          color: ${colors.white};
        `
      : css`
          background: ${props.theme.textStrongColor};
          border-color: ${props.theme.textStrongColor};
          color: ${props.theme.background};
        `)}
`;

// A category is one question rather than a list to work through: its card names what's in it, asks one question,
// folds its tasks away, and shrinks to a one-line summary once every task has a time. These are the Estimator's own
// pieces rather than SectionCard, CatHeader, DefaultRow, Pill or Toast, which the picker panels and the Plan dialogs
// share.
//
// Text keeps to three tones: textStrongColor for what to read (titles, the question), textColor for what's in the
// card, disabledTextColor for counts and side notes. Anything clickable is the theme colour.

// The card's task list as a plain accordion: one full-width row opens and closes the list under it, and nothing else
// on the card changes. The rows run edge to edge, as they did in the old section cards.
// --card-pad-x / --card-pad-y are the card's padding (EstimatorCard), so edge-to-edge parts can step out of it.
export const CardList = styled.div`
  margin: 14px calc(-1 * var(--card-pad-x)) calc(-1 * var(--card-pad-y));
  border-top: 1px solid ${props => props.theme.borderColor};
`;

// The part of a category card under its status (the question and the task list), which slides shut once every task
// has a time, so a pick reads as one motion: the pill turns green, the bar fills, and the card folds to its status.
// It spans the card's full width so the list's rows can still run edge to edge while it clips.
export const CardCollapse = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${props => (props.$open ? '1fr' : '0fr')};
  margin: 0 calc(-1 * var(--card-pad-x)) ${props => (props.$open ? 'calc(-1 * var(--card-pad-y))' : '0')};
  opacity: ${props => (props.$open ? 1 : 0)};
  transition: grid-template-rows 0.32s ease, margin-bottom 0.32s ease, opacity 0.24s ease;

  > div {
    min-height: 0;
    overflow: hidden;
    padding: 0 var(--card-pad-x) ${props => (props.$open ? 'var(--card-pad-y)' : '0')};
    transition: padding-bottom 0.32s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    > div {
      transition: none;
    }
  }
`;

export const ListToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 10px 18px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Quicksand', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.theme.primary};
  text-align: left;

  &:hover {
    background: ${props => `${props.theme.primary}0D`};
  }

  &:last-child {
    border-radius: 0 0 11px 11px;
  }
`;

export const ListBody = styled.div`
  border-top: 1px solid ${props => props.theme.borderColor};
`;

// Under the title: a small bar of how many tasks have a time, then the counts and the workload they add up to, at
// body size so it reads at a glance.
export const CardStatus = styled(Row)<{ $done?: boolean }>`
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  font-family: 'Quicksand', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${props => (props.$done ? colors.positive : props.theme.textColor)};

  b {
    font-weight: 700;
    color: ${props => (props.$done ? colors.positive : props.theme.textStrongColor)};
  }
`;

export const StatusTrack = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: ${props => props.theme.borderColor};
`;

export const StatusFill = styled.div<{ $pct: number }>`
  width: ${props => props.$pct}%;
  height: 100%;
  border-radius: 3px;
  background: ${colors.positive};
  transition: width 0.35s ease;
`;

// Over the pills: whether a pick also re-estimates tasks that already have a time.
export const IncludeRow = styled(Row)`
  width: 100%;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

export const IncludeLabel = styled(RegularText)`
  font-size: 13px;
  color: ${props => props.theme.textColor};
  cursor: pointer;
`;

export const EstimatorCard = styled.div<{ $current?: boolean; $done?: boolean; $clickable?: boolean }>`
  --card-pad-x: 18px;
  --card-pad-y: 16px;
  margin-bottom: 12px;
  padding: var(--card-pad-y) var(--card-pad-x);
  background: ${props =>
    props.$done
      ? props.theme.name === 'light'
        ? `${colors.lightPositive}66`
        : `${colors.positive}14`
      : props.theme.background};
  border: 1px solid ${props => (props.$done ? `${colors.positive}99` : props.theme.borderColor)};
  border-radius: 12px;
  transition: border-color 0.2s, background 0.2s;

  // A real 2px border, with a pixel less padding to match: an outline drawn outside the card was clipped at the sides
  // by the scrolling list.
  ${props =>
    props.$current &&
    css`
      --card-pad-x: 17px;
      --card-pad-y: 15px;
      border: 2px solid ${props.theme.primary};
    `}

  // A finished card is one big button that opens it.
  ${props =>
    props.$clickable &&
    css`
      cursor: pointer;

      &:hover,
      &:focus-visible {
        border-color: ${colors.positive};
        background: ${props.theme.name === 'light' ? `${colors.lightPositive}B3` : `${colors.positive}24`};
        outline: none;
      }
    `}
`;

export const CardHead = styled(Row)`
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const CardTitle = styled(Row)`
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const CardCount = styled(SmallText)`
  color: ${props => props.theme.disabledTextColor};
  white-space: nowrap;
`;

export const DoneSummary = styled(SmallText)`
  color: ${colors.positive};
  font-weight: 700;
  white-space: nowrap;
`;

export const CardLink = styled.button`
  font-family: 'Quicksand', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: ${props => props.theme.primary};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

// Pressable rather than outlined: theme-coloured text on a tint of it, filled solid on hover, until one is picked,
// when the shared Pill's own colours take over. The fill is instant, like the app's other buttons: a fade, however
// short, read as the pill lagging behind the pointer.
const pressablePills = css`
  button[data-selected='false'] {
    color: ${pillText};
    background: ${props => `${props.theme.primary}1F`};
    border-color: ${props => `${props.theme.primary}59`};

    &:hover {
      color: ${colors.white};
      background: ${props => props.theme.primary};
      border-color: ${props => props.theme.primary};
    }
  }
`;

// A card's one question and its answers: a category's typical time, or a book's time per page.
// Three steps down the card: the status, then the question with its details tucked under it in smaller text, then
// a clear gap before the answers.
export const QuestionRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 14px;

  & > ${PillGroup} {
    margin-top: 12px;
  }

  ${Pill} {
    font-size: 14px;
    font-weight: 700;
    padding: 8px 16px;
  }

  ${pressablePills}
`;

// The caption hangs under its pill without widening it, so the gaps between pills stay even.
export const PillWithCaption = styled.span`
  position: relative;
  display: inline-flex;
`;

export const PillCaption = styled(SmallText)`
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: ${props => props.theme.disabledTextColor};
  white-space: nowrap;
`;

export const DefaultQuestion = styled(RegularText)`
  width: 100%;
  font-size: 15px;
  line-height: 1.3;
  font-weight: 700;
  color: ${props => props.theme.textStrongColor};
`;

export const ListQuestion = styled(DefaultQuestion)`
  display: block;
  margin-top: 18px;
`;

export const CardHint = styled(SmallText)`
  width: 100%;
  margin-top: 4px;
  color: ${props => props.theme.disabledTextColor};
`;

// Sits beside the pills and only renders with a message, so the row keeps no empty line for it.
export const DefaultToast = styled(Toast)`
  width: auto;
  min-height: 0;
  margin-left: 4px;
`;

export const EstimatorIntro = styled.div`
  margin: 2px 2px 16px;
`;

export const IntroLead = styled(RegularText)`
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.textStrongColor};
`;

export const IntroBody = styled(RegularText)`
  display: block;
  margin-top: 2px;
  color: ${props => props.theme.textColor};
`;

export const CustomPop = styled(Row)`
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  padding-top: 8px;
  margin-top: 2px;
  border-top: 1px dashed ${props => props.theme.borderColor};
  align-items: center;
`;

export const FieldError = styled.div`
  width: 100%;
  font-size: 11px;
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
  color: ${colors.warning};
  min-height: 14px;
`;

export const PickerActions = styled(Row)`
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px solid ${props => props.theme.borderColor};
`;

export const PickerActionsLeft = styled(Row)`
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ResetLink = styled.button`
  font-family: 'Quicksand', sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: ${props => props.theme.primary};
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
`;

export const Sep = styled.span`
  color: ${props => props.theme.disabledTextColor};
  font-size: 11px;
  opacity: 0.6;
`;

export const ConvertChoices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

export const ConvertChoice = styled.button`
  font-family: 'Quicksand', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: ${props => props.theme.textStrongColor};
  padding: 6px 12px;
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 999px;
  background: ${props => props.theme.backgroundDark};
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.primary};
  }
`;

export const PagesInput = styled.input`
  width: 110px;
  font-family: 'Quicksand', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 8px;
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 6px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.textStrongColor};
  outline: none;

  // The app's placeholder grey, as every other field has; without it the browser's darker default read as typed text.
  &::placeholder {
    color: ${props => props.theme.placeholder};
    opacity: 1;
  }
`;

export const FooterStats = styled(RegularText)`
  color: ${props => props.theme.disabledTextColor};

  b {
    color: ${props => props.theme.textStrongColor};
    font-weight: 700;
  }
`;

export const DetectedMeta = styled(SmallText)<{ $struck?: boolean }>`
  margin-left: 8px;
  color: ${props => props.theme.disabledTextColor};
  ${props => props.$struck && 'text-decoration: line-through;'}
`;

export const EditedMeta = styled(SmallText)`
  margin-left: 6px;
  color: ${props => props.theme.primary};
`;
